import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { ArrowUpRight, Leaf, Recycle, Lightbulb, ShieldCheck, Factory, FileCheck } from "lucide-react";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import RelatedReading from "@/components/RelatedReading";

/**
 * Pillar / advisory page — "Sustainable Office Furniture (India)".
 *
 * Purpose: rank for ESG / green-procurement long-tail queries like
 *  "IGBC certified office furniture", "low-VOC office furniture India",
 *  "sustainable office furniture for green-rated buildings".
 *
 * Strategy: pure educational E-E-A-T content tying Ezeefit's verifiable
 *  IGBC Certificate of Compliance into corporate ESG procurement
 *  language. No fabricated claims, no certifications we don't hold.
 */

const PILLARS = [
  {
    title: "IGBC Certificate of Compliance",
    icon: ShieldCheck,
    body: "Ezeefit Office Systems holds an IGBC (Indian Green Building Council) Certificate of Compliance for its manufactured office furniture systems. This certificate validates that products meet the credit-eligible material requirements for IGBC Green Interiors and LEED v4 ID+C interior fit-outs — enabling the client building to claim sustainability credits on the procurement.",
  },
  {
    title: "Low-VOC laminates & adhesives",
    icon: Leaf,
    body: "All Ezeefit pre-laminated particle board (PLPB) and MDF substrates use E1-grade or better formaldehyde emission rating. Edge banding adhesives and finishing coats are selected for low Volatile Organic Compound (VOC) emission — the same specification range required by IGBC and LEED interior fit-out credits.",
  },
  {
    title: "Recycled & FSC-aligned wood content",
    icon: Recycle,
    body: "Ezeefit's particle-board substrates are manufactured from recycled wood fibre and post-industrial timber off-cuts. For projects requiring documented chain-of-custody, FSC-certified board can be specified at the project quote stage — typical lead-time addition is 5–10 days.",
  },
  {
    title: "Powder-coated steel (chrome-free)",
    icon: Factory,
    body: "Metal frames for workstation legs, partitions and storage units use CRCA steel with chrome-free, RoHS-compliant powder-coating. This avoids hexavalent-chrome runoff associated with traditional chrome-plating and meets the materials criteria for green-rated office interiors.",
  },
  {
    title: "Designed for disassembly & reuse",
    icon: Recycle,
    body: "Ezeefit modular furniture is designed for re-configuration — every component is bolted (not glued) and replaceable individually. This extends the service life of the furniture across multiple office moves and reduces end-of-life landfill volume, which counts toward IGBC Material & Resources credits.",
  },
  {
    title: "BEE-rated LED lighting in pods & booths",
    icon: Lightbulb,
    body: "Acoustic phone booths and meeting pods ship with BEE 5-star equivalent LED task lighting and motion-sensor activation — drawing power only when the booth is occupied. This contributes to the Energy & Atmosphere credit category for green-rated office interiors.",
  },
];

const CREDIT_TABLE = [
  { credit: "Low-emitting materials (PLPB, MDF)", standard: "IGBC GI / LEED v4 ID+C", evidence: "E1-grade formaldehyde emission certificates per material lot" },
  { credit: "Low-VOC adhesives & finishes", standard: "IGBC GI / LEED v4 ID+C", evidence: "Manufacturer SDS sheets on low-VOC compliance" },
  { credit: "Recycled content (wood substrate)", standard: "IGBC Material & Resources", evidence: "Particle-board mill certificates — recycled wood fibre %" },
  { credit: "FSC chain-of-custody (on request)", standard: "IGBC / LEED MR credit", evidence: "FSC certificates per board lot (project-specific spec)" },
  { credit: "Modular re-configurable design", standard: "IGBC MR — design for disassembly", evidence: "BOM with re-usable component listing" },
  { credit: "Motion-sensor LED lighting (pods)", standard: "IGBC Energy & Atmosphere", evidence: "BEE-equivalent rating certificates from LED supplier" },
];

const FAQS = [
  {
    q: "Is Ezeefit office furniture IGBC certified?",
    a: "Yes. Ezeefit Modular Furniture Private Limited holds an IGBC (Indian Green Building Council) Certificate of Compliance for its office furniture manufacturing line. The certificate confirms that Ezeefit furniture meets the material and emission requirements that enable client buildings to claim IGBC Green Interiors and LEED v4 ID+C credits during interior fit-out.",
  },
  {
    q: "What is low-VOC office furniture?",
    a: "Low-VOC office furniture uses laminates, particle-board substrates, adhesives and finishing coats with Volatile Organic Compound emissions below the threshold defined by green-building standards (IGBC Green Interiors, LEED v4 ID+C, WELL Building Standard). Low-VOC furniture improves indoor air quality and is mandatory for green-rated office interiors. Ezeefit's standard product line uses E1-grade boards and compatible adhesives that meet these emission caps.",
  },
  {
    q: "Can Ezeefit furniture help achieve LEED or IGBC certification for our office?",
    a: "Yes. The materials credits in LEED v4 Interior Design & Construction (ID+C) and IGBC Green Interiors reward fit-outs that use low-emitting, recycled-content, locally-manufactured furniture. Ezeefit furniture qualifies under multiple credit categories — Materials & Resources, Indoor Environmental Quality, and (for Mumbai/Maharashtra projects) Regional Materials. Ezeefit provides the supporting product datasheets, SDS sheets and IGBC compliance documentation as part of every project quote.",
  },
  {
    q: "Where is Ezeefit furniture manufactured?",
    a: "All Ezeefit office furniture is manufactured at the company's facility in Bhiwandi, Thane district, Maharashtra. For projects in Mumbai, Thane, Navi Mumbai, Pune and other Maharashtra cities, this counts as locally-manufactured furniture (within 800 km) for IGBC and LEED Regional Materials credits.",
  },
  {
    q: "Does Ezeefit offer FSC-certified furniture?",
    a: "FSC (Forest Stewardship Council) certified board can be specified at the project quote stage. This requires Ezeefit to procure FSC chain-of-custody documented particle-board or MDF from approved mills. Typical lead-time addition is 5–10 days, and pricing is approximately 10–20% above standard substrate. The resulting furniture carries FSC chain-of-custody documentation for the client's green-building submission.",
  },
  {
    q: "What is the environmental difference between PLPB and solid wood furniture?",
    a: "Pre-laminated particle board (PLPB) is manufactured from recycled wood fibre and timber off-cuts — typically 70–95% recycled content by weight. Solid wood furniture relies on freshly harvested timber. For corporate office fit-outs at 100+ seat scale, PLPB has a substantially lower embodied-carbon footprint per seat, and it qualifies for IGBC Materials & Resources credits that solid-wood furniture typically does not.",
  },
  {
    q: "Do Ezeefit acoustic pods qualify for green-building credits?",
    a: "Yes. Ezeefit acoustic phone booths and meeting pods ship with BEE-equivalent LED task lighting, motion-sensor occupancy activation and mechanical ventilation — all of which contribute to Energy & Atmosphere credit categories in IGBC and LEED interior fit-outs. The acoustic wall panels themselves use low-VOC fabric finishes that contribute to Indoor Environmental Quality credits.",
  },
  {
    q: "Can existing office furniture be reused or re-configured?",
    a: "Yes. Ezeefit modular furniture is designed for disassembly — every panel, leg, top and pedestal is individually bolted (not glued) and can be re-arranged, repaired or replaced without scrapping the full unit. This extends the service life of the furniture across multiple office moves and reduces the end-of-life landfill volume, which counts toward IGBC Materials & Resources design-for-disassembly credit.",
  },
];

const PageBlock = ({ icon: Icon, title, body }) => (
  <div className="flex gap-5 p-6 bg-white border border-[#E2E8F0] hover:border-[#F97316] transition-colors duration-300">
    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#0F172A] text-[#F97316]">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
    </div>
    <div className="flex-1">
      <h3 className="text-base font-semibold text-[#0F172A] mb-2">{title}</h3>
      <p className="text-sm text-[#475569] leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function SustainableOfficeFurniturePage() {
  const canonicalUrl = "https://ezeefitofficesystems.in/sustainable-office-furniture-india";

  useCanonical(canonicalUrl);
  useMetaDescription("IGBC certified office furniture by Ezeefit — low-VOC, recycled-content modular furniture for LEED & IGBC Green Interiors fit-outs. Manufactured in Bhiwandi.");
  useEffect(() => {
    document.title = "IGBC Certified Office Furniture India | Sustainable Workspaces | Ezeefit Office Systems";
  }, []);

  const breadcrumbJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://ezeefitofficesystems.in/" },
      { "@type": "ListItem", position: 2, name: "Sustainable Office Furniture", item: canonicalUrl },
    ],
  });

  const articleJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Sustainable Office Furniture for IGBC & LEED Office Fit-outs in India",
    description: "Technical reference for IGBC-compliant, low-VOC, recycled-content office furniture for green-rated corporate fit-outs in India — material credits, indoor air quality, design for disassembly and BEE-rated lighting.",
    author: { "@type": "Organization", name: "Ezeefit Office Systems" },
    publisher: { "@type": "Organization", name: "Ezeefit Office Systems", legalName: "Ezeefit Modular Furniture Private Limited" },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
  });

  const faqJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  });

  return (
    <div className="pt-20" data-testid="sustainable-office-furniture-page">
      <Helmet title="IGBC Certified Office Furniture India | Sustainable Workspaces | Ezeefit Office Systems">
        <meta name="description" content="IGBC certified office furniture by Ezeefit — low-VOC, recycled-content modular furniture for LEED & IGBC Green Interiors fit-outs. Manufactured in Bhiwandi." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="IGBC Certified Office Furniture India | Sustainable Workspaces | Ezeefit Office Systems" />
        <meta property="og:description" content="IGBC certified office furniture for green-rated corporate fit-outs in India — low-VOC, recycled-content, factory-direct from Bhiwandi. Material credits for LEED v4 ID+C." />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJson }} />

      {/* HERO */}
      <section className="relative bg-[#0F172A] overflow-hidden noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 lg:py-32 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-3 px-4 py-2 border border-[#F97316]/40 mb-8" data-testid="sustain-eyebrow">
              <span className="w-2 h-2 bg-[#F97316] rounded-full" />
              <span className="text-xs font-mono tracking-widest uppercase text-[#F97316]">ESG Procurement · IGBC Certified</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-8 max-w-4xl" data-testid="sustain-h1">
              Sustainable Office<br />
              <span className="text-[#F97316]">Furniture for India</span>
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-3xl">
              IGBC-compliant, low-VOC, recycled-content furniture for green-rated corporate fit-outs. Verifiable material credits for LEED v4 ID+C and IGBC Green Interiors — manufactured in Bhiwandi, India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ATOMIC ANSWER */}
      <section className="bg-white py-16 border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-[#F8FAFC] border-l-4 border-[#F97316] p-6 md:p-8" data-testid="sustain-atomic-answer">
            <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-3 block">In one paragraph</span>
            <p className="text-base md:text-lg text-[#0F172A] leading-relaxed">
              Ezeefit Modular Furniture Private Limited holds an <strong>IGBC Certificate of Compliance</strong>, validating that its office furniture meets the material and emission requirements for IGBC Green Interiors and LEED v4 ID+C credits. Standard products use <strong>E1-grade low-VOC substrates</strong>, recycled-content particle board, chrome-free powder-coated steel and BEE-equivalent LED lighting in pods — qualifying corporate fit-outs in Mumbai, Pune, Bangalore and pan-India for multiple green-rating credit categories.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#F8FAFC] py-20" data-testid="sustain-pillars-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-10">
            <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-2 block">Six Sustainability Pillars</span>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight">What makes Ezeefit furniture green-rated-ready</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <PageBlock key={i} icon={p.icon} title={p.title} body={p.body} />
            ))}
          </div>
        </div>
      </section>

      {/* CREDIT TABLE */}
      <section className="bg-white py-20" data-testid="sustain-credit-table-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] mb-3 tracking-tight">Green-Building Credit Mapping</h2>
          <p className="text-sm text-[#475569] mb-10 max-w-3xl">How Ezeefit furniture maps to IGBC Green Interiors and LEED v4 ID+C credit categories — with the supporting documentation provided as part of every project quote.</p>
          <div className="overflow-x-auto border border-[#E2E8F0]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#0F172A] text-left">
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Credit / Attribute</th>
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Standard</th>
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Documentation provided</th>
                </tr>
              </thead>
              <tbody>
                {CREDIT_TABLE.map((row, i) => (
                  <tr key={i} className="border-b border-[#E2E8F0] last:border-b-0">
                    <td className="px-5 py-3 text-[#0F172A] font-medium">{row.credit}</td>
                    <td className="px-5 py-3 text-[#475569] font-mono text-xs">{row.standard}</td>
                    <td className="px-5 py-3 text-[#64748B]">{row.evidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#F1F5F9] border border-[#E2E8F0] text-xs text-[#475569]">
            <FileCheck className="w-4 h-4 text-[#F97316]" strokeWidth={1.5} />
            All certificates verifiable. Issued to Ezeefit Modular Furniture Private Limited (GSTIN 27AADCE6117G1ZT).
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F8FAFC] py-20 border-t border-[#E2E8F0]" data-testid="sustain-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-3 block">Frequently Asked Questions</span>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-10">Sustainability &amp; Green-Building Q&amp;A</h2>
          <div className="space-y-4">
            {FAQS.map((item, i) => (
              <details key={i} className="group bg-white border border-[#E2E8F0] open:border-[#F97316] transition-colors" {...(i === 0 ? { open: true } : {})} data-testid={`sustain-faq-${i}`}>
                <summary className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-4">
                  <h3 className="text-base font-semibold text-[#0F172A] leading-snug">{item.q}</h3>
                  <span className="flex-shrink-0 w-6 h-6 border border-[#E2E8F0] flex items-center justify-center text-[#475569] group-open:bg-[#F97316] group-open:text-white group-open:border-[#F97316] transition-colors">
                    <span className="block group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                  </span>
                </summary>
                <div className="px-6 pb-5 -mt-1 text-sm text-[#475569] leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related Reading — bidirectional blog interlinking (SEO topic clusters) */}
      <RelatedReading
        keywords={["sustainable", "IGBC", "LEED", "green", "ESG", "recycled", "low-VOC"]}
        heading="Related Insights on Sustainable Office Furniture"
        subheading="Articles covering green-building certifications and material credits."
        testId="related-reading-pillar-sustainable"
      />

      {/* CTA */}
      <section className="bg-[#0F172A] py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-3">Specifying furniture for a green-rated office?</h2>
            <p className="text-[#94A3B8] text-base leading-relaxed">Share your IGBC / LEED scope — Ezeefit's project team will return a credit-mapped BOM with supporting documentation within one business day, free of cost.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold tracking-wide uppercase transition-colors" data-testid="sustain-cta-contact">
            Request a Green-Rated BOM
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
          </Link>
        </div>
      </section>
    </div>
  );
}
